import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  QrCodeScanner as ScannerIcon,
  PhotoCamera,
  Upload,
  SwitchCamera,
  Refresh
} from '@mui/icons-material';
import { BrowserMultiFormatReader } from '@zxing/browser';

interface BarcodeScannerProps {
  open: boolean;
  onClose: () => void;
  onScan: (result: string) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scanner-tabpanel-${index}`}
      aria-labelledby={`scanner-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ open, onClose, onScan }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [hasScanned, setHasScanned] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const callbackRef = useRef<boolean>(false);

  useEffect(() => {
    if (open) {
      setTabValue(0); // Reset to camera tab
      setError(null);
      setHasScanned(false);
      setIsStopping(false);
      callbackRef.current = false;
    } else {
      stopScanning();
    }

    return () => {
      // Aggressive cleanup when component unmounts or dialog closes
      callbackRef.current = true;
      setIsStopping(true);
      
      if (readerRef.current) {
        readerRef.current = null;
      }
      if (videoRef.current) {
        if (videoRef.current.srcObject) {
          const stream = videoRef.current.srcObject as MediaStream;
          stream.getTracks().forEach(track => {
            track.stop();
            track.enabled = false;
          });
          videoRef.current.srcObject = null;
        }
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      }
      setIsScanning(false);
    };
  }, [open]);

  const initializeScanner = async () => {
    try {
      setError(null);
      setHasScanned(false);
      setIsStopping(false);
      setIsScanning(true);
      callbackRef.current = false;

      // Create new reader instance (always create fresh instance)
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;

      // Get available video devices
      const videoInputDevices = await BrowserMultiFormatReader.listVideoInputDevices();
      setDevices(videoInputDevices);

      // Select device (prefer rear camera on mobile)
      let deviceId = '';
      if (videoInputDevices.length > 0) {
        // Try to find rear camera first
        const rearCamera = videoInputDevices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('environment')
        );
        deviceId = rearCamera ? rearCamera.deviceId : videoInputDevices[0].deviceId;
      }

      setSelectedDeviceId(deviceId);

      // Start scanning
      if (videoRef.current) {
        await reader.decodeFromVideoDevice(
          deviceId,
          videoRef.current,
          (result, error) => {
            // Prevent multiple callbacks
            if (callbackRef.current || isStopping) {
              return;
            }
            
            if (result && !hasScanned) {
              // Set flags to prevent multiple scans
              callbackRef.current = true;
              setHasScanned(true);
              setIsStopping(true);
              
              // Stop scanning immediately when result is found
              const scanResult = result.getText();
              stopScanning();
              
              // Use setTimeout to ensure cleanup happens before callback
              setTimeout(() => {
                onScan(scanResult);
              }, 100);
              return;
            }
            // Only log actual errors, not "no code found" messages
            if (error && !(error instanceof Error && (
              error.name === 'NotFoundException' || 
              error.message?.includes('No MultiFormat Readers were able to detect the code')
            ))) {
              console.error('Scanning error:', error);
            }
          }
        );
      }
    } catch (err) {
      console.error('Scanner initialization error:', err);
      setError('Failed to initialize camera. Please check permissions and try again.');
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    setIsStopping(true);
    callbackRef.current = true;
    
    if (readerRef.current) {
      try {
        // Stop the video stream and clean up
        if (videoRef.current) {
          if (videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject as MediaStream;
            stream.getTracks().forEach(track => {
              track.stop();
              track.enabled = false;
            });
            videoRef.current.srcObject = null;
          }
          // Clear the video element
          videoRef.current.pause();
          videoRef.current.removeAttribute('src');
          videoRef.current.load();
        }
      } catch (err) {
        console.warn('Error stopping scanner:', err);
      }
      readerRef.current = null;
    }
    setIsScanning(false);
  };

  const handleClose = () => {
    stopScanning();
    setError(null);
    setHasScanned(false);
    setIsStopping(false);
    callbackRef.current = false;
    onClose();
  };

  const switchCamera = async () => {
    if (devices.length <= 1) return;

    stopScanning();
    const currentIndex = devices.findIndex(device => device.deviceId === selectedDeviceId);
    const nextIndex = (currentIndex + 1) % devices.length;
    setSelectedDeviceId(devices[nextIndex].deviceId);
    
    setTimeout(() => {
      initializeScanner();
    }, 100);
  };

  const restartScanner = () => {
    stopScanning();
    setError(null);
    setHasScanned(false);
    setIsStopping(false);
    callbackRef.current = false;
    setTimeout(() => {
      initializeScanner();
    }, 100);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 0) {
      // Switching to camera tab
      setTimeout(() => {
        initializeScanner();
      }, 100);
    } else {
      // Switching to upload tab
      stopScanning();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, GIF, WebP)');
      // Reset file input only on validation error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size too large. Please select an image smaller than 10MB.');
      // Reset file input only on validation error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const reader = new BrowserMultiFormatReader();
      const imageUrl = URL.createObjectURL(file);
      
      const result = await reader.decodeFromImageUrl(imageUrl);
      
      // Clean up the object URL
      URL.revokeObjectURL(imageUrl);
      
      if (result) {
        onScan(result.getText());
        handleClose();
      } else {
        setError('No barcode found in the uploaded image. Please try a different image.');
        // Reset file input on scan failure
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (err) {
      console.error('Error processing uploaded image:', err);
      setError('Failed to read barcode from uploaded image. Please try a different image.');
      // Reset file input on processing error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCameraClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (fileInputRef.current && !isProcessing) {
      fileInputRef.current.click();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          Scan Barcode
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="scanner tabs">
            <Tab 
              icon={<CameraIcon />} 
              label="Live Camera" 
              iconPosition="start"
              sx={{ minHeight: 60 }}
            />
            <Tab 
              icon={<PhotoCamera />} 
              label="Încarcă Fotografia" 
              iconPosition="start"
              sx={{ minHeight: 60 }}
            />
          </Tabs>
        </Box>

        {/* Camera Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ position: 'relative', minHeight: 400 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 400,
                backgroundColor: '#000',
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <video
                ref={videoRef}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                playsInline
                muted
              />

              {/* Scanning overlay */}
              {isScanning && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 200,
                    height: 200,
                    border: '2px solid #1976d2',
                    borderRadius: 2,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': {
                        boxShadow: '0 0 0 0 rgba(25, 118, 211, 0.7)',
                      },
                      '70%': {
                        boxShadow: '0 0 0 10px rgba(25, 118, 211, 0)',
                      },
                      '100%': {
                        boxShadow: '0 0 0 0 rgba(25, 118, 211, 0)',
                      },
                    },
                  }}
                />
              )}

              {!isScanning && !error && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    color: 'white'
                  }}
                >
                  <ScannerIcon sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="h6">Camera not active</Typography>
                  <Typography variant="body2">Click "Start Camera" to begin scanning</Typography>
                </Box>
              )}
            </Box>

            {/* Camera Controls */}
            <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
              {!isScanning ? (
                <Button
                  variant="contained"
                  startIcon={<CameraIcon />}
                  onClick={initializeScanner}
                  disabled={isStopping}
                >
                  Start Camera
                </Button>
              ) : (
                <>
                  {devices.length > 1 && (
                    <Button
                      variant="outlined"
                      startIcon={<SwitchCamera />}
                      onClick={switchCamera}
                    >
                      Switch Camera
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={restartScanner}
                  >
                    Restart
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        </TabPanel>

        {/* Upload Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ textAlign: 'center', minHeight: 400 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Paper
              sx={{
                p: 4,
                border: '2px dashed #ccc',
                borderRadius: 2,
                backgroundColor: '#fafafa',
                transition: 'all 0.3s ease'
              }}
            >
              {isProcessing ? (
                <Box>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="h6">Se procesează imaginea...</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Te rugăm să aștepți în timp ce scanez pentru coduri de bare
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Upload sx={{ fontSize: 64, color: '#1976d2', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Încarcă Fotografia
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Selectează o imagine care conține un cod de bare pentru scanare
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PhotoCamera />}
                    onClick={handleCameraClick}
                  >
                    Alege Fișierul
                  </Button>
                </Box>
              )}
            </Paper>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Formate suportate: JPG, PNG, GIF, WebP
            </Typography>
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Anulează
        </Button>
      </DialogActions>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
    </Dialog>
  );
};

export default BarcodeScanner;